import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Markdown } from "@/lib/markdown";
import { Pagination, usePaged } from "@/components/Pagination";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }); } catch { return ""; } };

function Cover({ src, alt, className = "" }) {
  return src
    ? <img src={fileUrl(src)} alt={alt} className={`w-full h-full object-cover ${className}`} loading="lazy"/>
    : <div className={`w-full h-full bg-gradient-to-br from-[#0E4D34] to-[#2D7A5B] flex items-center justify-center ${className}`}><span className="text-white/70 text-5xl font-semibold">AN</span></div>;
}

function Meta({ post, light }) {
  return (
    <div className={`flex items-center gap-2 text-xs flex-wrap ${light ? "text-white/80" : "text-neutral-500"}`}>
      <span>{fmtDate(post.published_at || post.created_at)}</span>
      <span>·</span>
      <span className="inline-flex items-center gap-1"><Clock size={12}/>{post.reading_minutes || 1} min read</span>
      {post.author && <><span>·</span><span>{post.author}</span></>}
    </div>
  );
}

export function BlogList() {
  const [params, setParams] = useSearchParams();
  const tag = params.get("tag") || "";
  const { data: posts, isLoading } = useQuery({ queryKey: ["blog"], queryFn: () => api.get("/blog").then((r) => r.data) });
  const all = posts || [];
  const tags = [...new Set(all.flatMap((p) => p.tags || []))].sort();
  const list = tag ? all.filter((p) => (p.tags || []).includes(tag)) : all;
  // 10 posts per page; the newest post on page 1 is shown large
  const pg = usePaged(list, 10, { resetKey: tag, scrollTop: true });
  const [first, ...rest] = pg.page === 1 ? pg.items : [null, ...pg.items];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-14">
      <div className="mb-8">
        <div className="overline mb-3">The Journal</div>
        <h1 className="h1-editorial mb-3">Blog</h1>
        <p className="text-neutral-600 max-w-2xl">Saree care, styling ideas, weaving stories and what's new at the boutique.</p>
      </div>

      {tags.length > 0 && (
        <div className="flex gap-2 flex-wrap mb-8">
          <button onClick={() => setParams({})} className={`rounded-full border px-4 py-1.5 text-sm ${!tag ? "bg-[#0E4D34] border-[#0E4D34] text-white" : "border-neutral-300 hover:border-[#0E4D34]"}`}>All</button>
          {tags.map((t) => (
            <button key={t} onClick={() => setParams({ tag: t })} className={`rounded-full border px-4 py-1.5 text-sm ${tag === t ? "bg-[#0E4D34] border-[#0E4D34] text-white" : "border-neutral-300 hover:border-[#0E4D34]"}`}>{t}</button>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="grid md:grid-cols-3 gap-6">{[0, 1, 2].map((i) => <div key={i} className="aspect-[4/3] rounded-2xl bg-neutral-200 animate-pulse"/>)}</div>
      ) : list.length === 0 ? (
        <div className="bg-white border rounded-2xl p-12 text-center text-neutral-500">No posts yet — check back soon.</div>
      ) : (
        <>
          {/* Latest post, large */}
          {first && <Link to={`/blog/${first.slug}`} className="group grid md:grid-cols-2 gap-6 lg:gap-10 items-center mb-12 bg-white rounded-3xl border overflow-hidden" data-testid="blog-featured">
            <div className="aspect-[4/3] md:aspect-auto md:h-full md:min-h-[320px] overflow-hidden"><Cover src={first.cover_image} alt={first.title} className="group-hover:scale-[1.03] transition-transform duration-500"/></div>
            <div className="p-6 md:p-8 md:pl-0">
              {(first.tags || []).length > 0 && <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0E4D34] mb-3">{first.tags[0]}</div>}
              <h2 className="text-2xl sm:text-3xl font-semibold text-neutral-900 leading-tight mb-3 group-hover:text-[#0E4D34]">{first.title}</h2>
              <p className="text-neutral-600 mb-4 line-clamp-3">{first.excerpt}</p>
              <Meta post={first}/>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0E4D34]">Read article<ArrowRight size={14}/></span>
            </div>
          </Link>}
          {rest.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {rest.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="group flex flex-col" data-testid="blog-card">
                  <div className="aspect-[4/3] rounded-2xl overflow-hidden mb-4"><Cover src={p.cover_image} alt={p.title} className="group-hover:scale-[1.03] transition-transform duration-500"/></div>
                  {(p.tags || []).length > 0 && <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#0E4D34] mb-2">{p.tags[0]}</div>}
                  <h3 className="text-xl font-semibold text-neutral-900 leading-snug mb-2 group-hover:text-[#0E4D34]">{p.title}</h3>
                  <p className="text-sm text-neutral-600 line-clamp-2 mb-3">{p.excerpt}</p>
                  <Meta post={p}/>
                </Link>
              ))}
            </div>
          )}
          <Pagination {...pg.props} label="posts"/>
        </>
      )}
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const { data: post, isLoading, error } = useQuery({ queryKey: ["blog-post", slug], queryFn: () => api.get(`/blog/${slug}`).then((r) => r.data), retry: false });
  React.useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (isLoading) return <div className="max-w-3xl mx-auto px-4 py-20"><div className="h-10 bg-neutral-200 rounded animate-pulse mb-4"/><div className="h-64 bg-neutral-200 rounded-2xl animate-pulse"/></div>;
  if (error || !post) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center">
      <h1 className="h2-editorial mb-3">Post not found</h1>
      <Link to="/blog" className="btn-silk rounded-[10px]">Back to the blog</Link>
    </div>
  );
  return (
    <article className="pb-10" data-testid="blog-post">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-[#0E4D34] mb-6"><ArrowLeft size={14}/>All posts</Link>
        {(post.tags || []).length > 0 && (
          <div className="flex gap-2 flex-wrap mb-4">{post.tags.map((t) => <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`} className="rounded-full bg-[#E8F3EE] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#0E4D34] hover:bg-[#d6ebe1]">{t}</Link>)}</div>
        )}
        <h1 className="text-3xl sm:text-5xl font-semibold text-neutral-900 leading-tight tracking-tight mb-4">{post.title}</h1>
        <Meta post={post}/>
      </div>
      {post.cover_image && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-8">
          <img src={fileUrl(post.cover_image)} alt={post.title} className="w-full max-h-[560px] object-cover rounded-3xl"/>
        </div>
      )}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-10">
        <Markdown text={post.content}/>
      </div>
      {(post.more || []).length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-16 pt-10 border-t">
          <h2 className="text-2xl font-semibold mb-6">More from the blog</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {post.more.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="group">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden mb-3"><Cover src={p.cover_image} alt={p.title} className="group-hover:scale-[1.03] transition-transform duration-500"/></div>
                <h3 className="text-lg font-semibold group-hover:text-[#0E4D34]">{p.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
